import React, { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { selectLoginSliceIsLoading, selectUser } from '../Login/LoginSlice';
import { changeProfileImage, refreshUser } from '../Login/LoginSlice/actionCreators';
import userImg from '../../assets/icons/silhouette-with-background.svg';
import EditImg from '../../assets/icons/pen-with-line-round.svg';
import LockImg from '../../assets/icons/lock-with-three-dots.svg';
import ArrowRightImg from '../../assets/icons/chevron-right.svg';
import Loader from '../../components/Loader/Loader';
import { translate } from '../../utils/translate';
import { Page } from '../../components/Page/Page';
import { ChoosePhotoBottomSheet } from '../../components/ChoosePhotoBottomSheet/ChoosePhotoBottomSheet';
import { uploadImage } from '../../apis/mediaAPI';
import {
  getPhoneNumber,
  getPhoneNumberStringByPhoneNumberObject,
} from '../ChangeSecondaryNumber/helpers';
import {
  SUserImgContainer,
  SBottomText,
  SEditImage,
  SLink,
  SLinkContainer,
  SPropertyDisplay,
  STextContainer,
  STextFieldEndPlaceHolder,
  SUserImg,
  SUserProfileAvailabilityContainer,
} from './Profile.styles';
import UserProfileAvailabilityButton from '../../components/UserProfileAvailabilityButton/UserProfileAvailabilityButton';
import { checkIsAvailable } from '../../utils/helperFunctions';

export const Profile = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const isLoading = useAppSelector(selectLoginSliceIsLoading);

  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [isLoadingPic, setIsLoadingPic] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);
  const [profilePic, setProfilePic] = React.useState<string | undefined>(user?.imageUrls?.small);

  const { primaryPhoneNumber, secondPhoneNumber } = useMemo(() => {
    return {
      primaryPhoneNumber: getPhoneNumber(user?.phoneNumbers?.primary || ''),
      secondPhoneNumber: getPhoneNumber(user?.phoneNumbers?.secondary || ''),
    };
  }, [user?.phoneNumbers]);

  const updateProfileImage = (uploadedImageUrl: string | 'DELETE_PROFILE_PIC') => {
    if (uploadedImageUrl) {
      try {
        setIsLoadingPic(true);
        dispatch(
          changeProfileImage(uploadedImageUrl === 'DELETE_PROFILE_PIC' ? null : uploadedImageUrl)
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
        ).then(() => {
          setIsLoadingPic(false);
        });
      } catch (e) {
        setIsLoadingPic(false);
      }
    }
  };

  useEffect(() => {
    dispatch(refreshUser());
  }, [dispatch]);

  const deletePic = () => {
    setProfilePic(undefined);
    updateProfileImage('DELETE_PROFILE_PIC');
  };

  const toggleIsOpen = React.useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const onImagePicked = async (picUrl: string, picFile?: File) => {
    setProfilePic(picUrl);
    if (picFile) {
      const imageFormData = new FormData();
      imageFormData.append('image', picFile, picFile.name);
      try {
        setIsUploading(true);
        const res = await uploadImage(imageFormData);
        if (res.URL) {
          updateProfileImage(res.URL);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setIsUploading(false);
      }
    }
  };

  if (isLoading || isLoadingPic || isUploading) return <Loader />;

  if (user) {
    return (
      <Page>
        <SUserImgContainer onClick={() => setIsOpen(!isOpen)} role="userImageContainer">
          <SUserImg
            src={user?.imageUrls?.small ? user.imageUrls.small : profilePic ? profilePic : userImg}
            alt=""
            role="userImage"
          />
          <SEditImage src={EditImg} alt="edit" role="editIcon" />
        </SUserImgContainer>
        <SUserProfileAvailabilityContainer>
          <UserProfileAvailabilityButton
            available={checkIsAvailable(user?.unavailableUntil)}
            unavailableDateTo={user?.unavailableUntil}
            allowEdit
          />
        </SUserProfileAvailabilityContainer>

        <STextContainer role="infoContainer">
          <STextFieldEndPlaceHolder
            className="wrapper"
            $content={user.name ? undefined : translate('optional-brackets')}
            role="groupInfoNameContainer"
          >
            <SPropertyDisplay role="inputField">
              <label role="label">{translate('groupInfoName')}: </label>
              <p className="valueText" role="email">
                {user.name}
              </p>
            </SPropertyDisplay>
          </STextFieldEndPlaceHolder>
          <STextFieldEndPlaceHolder className="wrapper" role="groupMembersInfoContainer">
            <SPropertyDisplay role="inputField">
              <label role="label">{translate('groups_member_additionalInfo')}: </label>
              <p className="valueText" role="email">
                {user.title}
              </p>
            </SPropertyDisplay>
          </STextFieldEndPlaceHolder>

          <STextFieldEndPlaceHolder className="wrapper" role="emailContainer">
            <SPropertyDisplay role="inputField">
              <label role="label">{translate('profile_email')}: </label>
              <p className="valueText" role="email">
                {user.email}
              </p>
            </SPropertyDisplay>
          </STextFieldEndPlaceHolder>

          <SLinkContainer className="wrapper" role="phone1Container">
            <SLink $nonPointer to="" role="phone1Link">
              <label role="label">{translate('profile_info_phone')}: </label>
              <p className="phone-number" role="phoneNumber">
                {getPhoneNumberStringByPhoneNumberObject(primaryPhoneNumber)}
              </p>
            </SLink>
          </SLinkContainer>
          <SLinkContainer linkMode className="wrapper" role="phone2Container">
            <SLink
              $placeHolder={!secondPhoneNumber?.nationalNumber}
              to="/change-second-number"
              role="phone2Link"
            >
              <label role="label">{translate('profile_info_phone')}: </label>
              <p className="phone-number" role="phoneNumber">
                {getPhoneNumberStringByPhoneNumberObject(secondPhoneNumber)}
              </p>

              <img className="to-edit" src={ArrowRightImg} alt="edit" role="icon" />
            </SLink>
          </SLinkContainer>

          <SLink to="/change_password" className="wrapper" role="changePassword">
            <img src={LockImg} alt="lock" role="lockIcon" />
            {translate('profile_edit_password')}
            <img className="to-edit" src={ArrowRightImg} alt="edit" role="editIcon" />
          </SLink>
        </STextContainer>
        <ChoosePhotoBottomSheet
          isOpen={isOpen}
          toggleIsOpen={toggleIsOpen}
          onDeletePic={deletePic}
          didChoosePic={onImagePicked}
          choosedPic={!!profilePic || !!user?.imageUrls?.small}
        />
        <SBottomText role="versionText">
          {translate('profile_info_version')}: {process.env.REACT_APP_COSAFE_LATEST_VERSION}
        </SBottomText>
      </Page>
    );
  } else {
    return <></>;
  }
};
